import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Smartphone, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

// V25 C9 — superadmin editor for GET /api/app/config (mobile remote config).
export const AppRemoteConfigCard = () => {
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get('/api/admin/app-remote-config').then((res) => {
    setData(res.data);
    setText(JSON.stringify(res.data.overrides || {}, null, 2));
  }).catch(() => toast.error('Could not load remote config'));

  useEffect(() => { load(); }, []);

  const save = async () => {
    let parsed;
    try {
      parsed = text.trim() ? JSON.parse(text) : {};
      if (typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Must be a JSON object');
    } catch (e) {
      setError(e.message);
      return;
    }
    setError('');
    setSaving(true);
    try {
      const res = await api.put('/api/admin/app-remote-config', { config: parsed });
      setData(res.data);
      setText(JSON.stringify(res.data.overrides || {}, null, 2));
      toast.success('App remote config saved — live within an hour (Cache-Control) or on next app launch');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-2xl" data-testid="app-remote-config-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Smartphone className="h-5 w-5" />App Remote Config
          <Badge variant="outline" className="ml-2 text-[10px]">GET /api/app/config</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Partial overrides for the mobile app (renewal nudge thresholds, trial timeslots, postnatal yoga copy).
          Sections shallow-merge over the defaults below; arrays replace wholesale. No store release needed.
        </p>
        <textarea
          className="w-full min-h-[180px] rounded-xl border bg-background p-3 font-mono text-xs"
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          data-testid="app-remote-config-textarea"
        />
        {error && <p className="text-xs text-rose-600" data-testid="app-remote-config-error">{error}</p>}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving} data-testid="app-remote-config-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save overrides
          </Button>
          <Button size="sm" variant="outline" onClick={() => setText('{}')} data-testid="app-remote-config-reset">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset to defaults
          </Button>
          {data?.updated_at && <span className="text-[11px] text-muted-foreground">Last saved {new Date(data.updated_at).toLocaleString()}</span>}
        </div>
        {data?.config && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">Effective config the app receives</summary>
            <pre className="mt-2 rounded-xl bg-muted/40 p-3 overflow-auto max-h-64" data-testid="app-remote-config-effective">{JSON.stringify(data.config, null, 2)}</pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
};
